/**
 * Policy wire types — shared between apps/api and apps/web.
 *
 * Single source of truth for `PolicyStatus`, `LineOfBusiness`, `PaymentFrequency`
 * and the DTO shapes consumed by the 9.2 controller (coding-standards §1:
 * "Statuses … → named constants/enums in packages/shared, never string/number literals").
 *
 * String-union types mirror the pgEnum values EXACTLY (no magic values at call sites).
 *
 * [Source: docs/stories/9.1-policy-version-schema.md#task-3]
 * [Source: packages/db/src/schema/_enums.ts#policyStatus+lineOfBusiness+paymentFrequency]
 * [Source: packages/shared/src/task.ts]  (shared-type precedent)
 * [Source: packages/shared/src/firms.ts] (DTO shape precedent)
 * [Source: docs/architecture/coding-standards.md#1] (no magic values)
 */

import { zurichDateOf } from './zurich-day';
import type { PageMeta } from './pagination';

// ── Status / enum unions ──────────────────────────────────────────────────────

import type { PolicyRegistrationStatus } from './mandate-registration-reply';

/**
 * Policy lifecycle status — mirrors `policy_status` Postgres enum.
 * APPEND-ONLY: never remove or reorder values.
 */
export type PolicyStatus =
  | 'draft'
  | 'active'
  | 'in_mutation'
  | 'renewal_pending'
  | 'cancelled'
  | 'expired';

/**
 * Line of business / Sparte — mirrors `line_of_business` Postgres enum.
 * UVG / KTG / BU are the wage-based Sparten consumed by the Schlussabrechnung.
 * Extend append-only.
 */
export type LineOfBusiness =
  | 'uvg'         // Unfallversicherung — wage-based
  | 'ktg'         // Krankentaggeld — wage-based
  | 'bu'          // Berufsunfähigkeit — wage-based
  | 'leben'       // Lebensversicherung
  | 'sach'        // Sachversicherung
  | 'haftpflicht' // Haftpflichtversicherung
  | 'kranken'     // Krankenzusatzversicherung
  | 'other';

/**
 * Payment frequency / Zahlweise — mirrors `payment_frequency` Postgres enum.
 * ASCII identifiers; labels are displayed in German in the UI.
 */
export type PaymentFrequency =
  | 'jaehrlich'     // jährlich
  | 'halbjaehrlich' // halbjährlich
  | 'quartalsweise'
  | 'monatlich'
  | 'einmalig';     // Einmalprämie

/**
 * Subject kind for the holder (VN / Versicherungsnehmer) or linked subject.
 * Mirrors the `subject_kind` Postgres enum.
 * 'invoice' appended (Story 10.2): task.subject_type = 'invoice' for the no-hit matching Task.
 */
export type SubjectKind = 'firma' | 'person' | 'invoice';

/**
 * LINE_OF_BUSINESS_LABELS — German display label per Sparte (Story GAP-B4).
 *
 * The single source of truth for the Branche/Sparte column label rendered on the
 * Policen grid. Labels come from the `LineOfBusiness` enum's own doc comments
 * (identifiers stay ASCII, labels are German — coding-standards §1). The list
 * read falls back to the raw `igb2b_branch` code when `line_of_business` is null,
 * then to `null`.
 *
 * [Source: packages/shared/src/policy.ts#LineOfBusiness]
 * [Source: docs/stories/gap-b4-policy-wire-fields.md AC1/AC5]
 */
export const LINE_OF_BUSINESS_LABELS: Record<LineOfBusiness, string> = {
  uvg:         'Unfallversicherung (UVG)',
  ktg:         'Krankentaggeld (KTG)',
  bu:          'Berufsunfähigkeit (BU)',
  leben:       'Lebensversicherung',
  sach:        'Sachversicherung',
  haftpflicht: 'Haftpflichtversicherung',
  kranken:     'Krankenzusatzversicherung',
  other:       'Übrige',
};

/**
 * PAYMENT_FREQUENCY_LABELS — German display label per Zahlungsart (Story P2-2/P2-4, WS4 §4.1).
 *
 * `policy.payment_frequency` is already auto-filled from Docpier at ingest (§0) — this map is
 * the missing display resolution for the policy-detail Stammdaten card, same precedent as
 * `LINE_OF_BUSINESS_LABELS` for the Sparte column.
 *
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §4.1]
 */
export const PAYMENT_FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  jaehrlich:     'jährlich',
  halbjaehrlich: 'halbjährlich',
  quartalsweise: 'quartalsweise',
  monatlich:     'monatlich',
  einmalig:      'Einmalprämie',
};

/**
 * PolicyRisikostufe — the AVB-15.3 Risikoeinschätzung level (Story GAP-B4).
 *
 * ASCII identifiers mirroring the V10.1 15.3 Risiko-Flag vocabulary
 * ("unauffällig / näher prüfen / kritisch") — the `gekuendigt` precedent (labels
 * German, identifiers ASCII — coding-standards §1). APPEND-ONLY union.
 *
 * ⚠️ There is NO Risikostufe backing data anywhere in the schema yet — the
 * `PolicyListItemDto.risikostufe` field is wire-ready but ALWAYS `null` in this
 * story. When the AVB-15.3 Risikoeinschätzung module (G14) lands it fills the
 * field; the wire contract + the `policen.risiko` visibility gate are ready NOW.
 *
 * [Source: docs/concept/2026-07-15_stefan-concept_V10.1.txt:394 (Risiko-Flag vocab)]
 * [Source: docs/stories/gap-b4-policy-wire-fields.md AC1]
 */
export type PolicyRisikostufe = 'unauffaellig' | 'naeher_pruefen' | 'kritisch';

// ── DTO shapes ────────────────────────────────────────────────────────────────

/**
 * PolicyDto — the policy read shape returned by the 9.2 controller.
 *
 * `tenant_id` is NOT included (implicit from token; never returned to clients).
 * `deleted_at` is NOT included (soft-delete is internal; clients see `status` only).
 * Money columns are strings (JSON doesn't have a decimal type — numeric(14,2) → string).
 */
export interface PolicyDto {
  id: string;
  carrier_uid: string | null;
  subject_type: SubjectKind;
  subject_id: string;
  policy_number: string;
  line_of_business: LineOfBusiness | null;
  igb2b_branch: string | null;
  /**
   * The IGB2B category code (`policy.igb2b_code`, FK → igb2b_kategorie; migration 0121). Distinct
   * from the raw `igb2b_branch` carrier code: this is the classified Sparte key P1 backfills for
   * haftpflicht/leben/kranken/sach to their family heads. `null` on unclassified policies. Broker-
   * overridable via PATCH /policies/:id (P2 Placement Slice 2 reclassification).
   */
  igb2b_code: string | null;
  broker_owner: string | null;
  mandate_id: string | null;
  inception_date: string | null;   // ISO 8601 date string
  expiry_date: string | null;      // ISO 8601 date string
  status: PolicyStatus;
  currency: string;
  payment_frequency: PaymentFrequency | null;
  iban: string | null;
  qr_reference: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Auto-generated policy label (Story P2-2/P2-3, WS4 §4.2) — `buildPolicyBezeichnung`
   * BACKFILL-ONLY: set at CREATE when null; a re-version never overwrites a broker-set value.
   * Also broker-overridable via `PATCH /policies/:id` (`UpdatePolicyDto`). `null` until the
   * first auto-fill (e.g. the motor plate+vehicle source is deferred — §4.2 — until Docpier
   * returns `coverage_json`).
   */
  bezeichnung: string | null;
  /**
   * Prämienbefreiung (premium waiver) — broker-set, MANUAL-ONLY (never written by ingest).
   * `policy.praemienbefreiung boolean NOT NULL DEFAULT false` — always present on the wire.
   */
  praemienbefreiung: boolean;
  /** Prämienbefreiung start date (ISO 8601 date string). `null` unless `praemienbefreiung=true`. */
  praemienbefreiung_seit: string | null;
  /**
   * Versicherungsvermittler — a VALUE-LINK to `person.id` (no hard FK; person is
   * soft-deletable, mirrors `customer_state.subject_id`). Distinct from `broker_owner`
   * (the internal Betreuer/app_user). `null` when unset — manual-only, never written by ingest.
   */
  vermittler_person_id: string | null;
  /**
   * Free-text Bemerkungen zur Police. First-class domain column (like `firma.notes`/
   * `person.notes`) — NEVER copied into audit/task jsonb (rule #4). Manual-only.
   */
  bemerkungen: string | null;
  /**
   * F4 (migration 0169) — Courtage eligibility window (inclusive). `courtage_eligible_from` is
   * the first day Courtage is claimable under the broker mandate (set by P5 registration); `_to`
   * the last day (set by P6 Austragung). `null` from = not (yet) eligible; `null` to = open-ended.
   * ISO 8601 date strings. Written exclusively via `PolicyService.setCourtageEligibilityTx`.
   */
  courtage_eligible_from: string | null;
  courtage_eligible_to: string | null;
  /**
   * P5-S7: only on the answer of POST /policies/:id/activate: the Vererbung hook failed on the activation
   * (the policy stands; a task «Vererbung prüfen» is open).
   */
  registration_hook_failed?: boolean;
}

/**
 * F4 — in-process domain-event names, carried on the activity row (placement convention; no bus).
 * There is no event emitter / outbox in this repo: the activity row IS the trail + the hook point.
 */
export const POLICY_EVENTS = {
  COURTAGE_ELIGIBILITY_SET:   'policy.courtage_eligibility_set',    // from (re)set
  COURTAGE_ELIGIBILITY_ENDED: 'policy.courtage_eligibility_ended',  // to set
} as const;
export type PolicyEvent = (typeof POLICY_EVENTS)[keyof typeof POLICY_EVENTS];

/**
 * The F4 courtage window as the courtage control reads it. A policy linked to a Brokermandat
 * registration (`registration_id` set) is courtage-bearing for a period only when its window overlaps
 * the period: `courtage_eligible_from IS NOT NULL AND from <= period_to AND (to IS NULL OR to >= period_from)`.
 * A policy without a registration link keeps the behaviour from before the registrations (always
 * courtage-bearing), because nothing writes the window for it. The dates are inclusive ISO dates; a
 * Wirkungsdatum inside the period makes the whole period courtage-bearing (no pro-rating).
 */
export interface CourtageWindowFields {
  registration_id?: string | null;
  courtage_eligible_from?: string | Date | null;
  courtage_eligible_to?: string | Date | null;
}

/**
 * A courtage-window or period date as `YYYY-MM-DD`. Text is cut to its date part. A Date stands for
 * a Postgres `date` read without `::text`, whose calendar day the drivers encode differently:
 * node-postgres builds local midnight, PGlite UTC midnight. So a Date at local midnight gives its
 * local day, one at UTC midnight its UTC day, and any other instant its Swiss calendar day. The
 * result does not depend on the process time zone. SQL readers should still prefer `::text`.
 */
export function courtageDateIso(value: string | Date): string {
  if (typeof value === 'string') return value.slice(0, 10);
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (value.getHours() === 0 && value.getMinutes() === 0 && value.getSeconds() === 0 && value.getMilliseconds() === 0) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  if (value.getUTCHours() === 0 && value.getUTCMinutes() === 0 && value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0) {
    return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
  }
  return zurichDateOf(value);
}

/**
 * Why a policy is not courtage-bearing for a billed period (stored next to the line-check finding
 * `courtage_not_eligible`): no confirmed registration (no `from`), the window starts after the period,
 * it ended before it, or the line names no period. An unlinked policy is bound only by its `to`
 * (decision 29.10.2026): `courtage_window_ended` past it, else eligible.
 */
export type CourtageNotEligibleReason =
  | 'courtage_window_no_registration'
  | 'courtage_window_not_started'
  | 'courtage_window_ended'
  | 'courtage_window_no_period';

/**
 * The reason a policy is not courtage-bearing for the period, or null when it is. A linked policy needs
 * its window to overlap the period; an unlinked one ignores `from` (P5-S9 B4) and honours only `to`
 * (P6-S5 decision 29.10.2026: the Freigabe ends it too).
 */
export function courtageNotEligibleReason(
  p: CourtageWindowFields,
  periodFrom: string | Date | null | undefined,
  periodTo: string | Date | null | undefined,
): CourtageNotEligibleReason | null {
  const pFrom = periodFrom ?? periodTo ?? null;
  const pTo = periodTo ?? periodFrom ?? null;
  if (p.registration_id == null) {
    const end = p.courtage_eligible_to ?? null;
    return end != null && pFrom != null && courtageDateIso(end) < courtageDateIso(pFrom) ? 'courtage_window_ended' : null;
  }
  if (pFrom == null || pTo == null) return 'courtage_window_no_period';
  const from = p.courtage_eligible_from ?? null;
  const to = p.courtage_eligible_to ?? null;
  if (from == null) return 'courtage_window_no_registration';
  if (courtageDateIso(from) > courtageDateIso(pTo)) return 'courtage_window_not_started';
  if (to != null && courtageDateIso(to) < courtageDateIso(pFrom)) return 'courtage_window_ended';
  return null;
}

export function isCourtageEligibleForPeriod(
  p: CourtageWindowFields,
  periodFrom: string | Date,
  periodTo: string | Date,
): boolean {
  return courtageNotEligibleReason(p, periodFrom, periodTo) == null;
}

/**
 * The same rule as SQL. `from` and `to` are parameter placeholders (`$2`), never values; `alias` is
 * the policy table's alias in the query (empty for none). A policy without a registration link is bound
 * only by its `to` (decision 29.10.2026).
 */
export function courtageWindowSql(from: string, to: string, alias = ''): string {
  const c = alias ? `${alias}.` : '';
  return (
    `((${c}registration_id IS NULL AND (${c}courtage_eligible_to IS NULL OR ${c}courtage_eligible_to >= ${from}::date))` +
    ` OR (${c}registration_id IS NOT NULL AND ${c}courtage_eligible_from IS NOT NULL` +
    ` AND ${c}courtage_eligible_from <= ${to}::date` +
    ` AND (${c}courtage_eligible_to IS NULL OR ${c}courtage_eligible_to >= ${from}::date)))`
  );
}

/** F4 — activity.meta shape for both POLICY_EVENTS (ids/dates only, no PII). */
export interface PolicyCourtageEligibilityEventMeta {
  event: PolicyEvent;
  policy_id: string;
  mandate_id: string | null;
  courtage_eligible_from: string | null;
  courtage_eligible_to: string | null;
  source: 'p5_registration' | 'p6_termination' | 'manual';
}

/**
 * UpdatePolicyDto — the `PATCH /policies/:id` request body (Story P2-3, WS4 §4.6).
 *
 * All-optional — a PATCH updates only the keys present in the body. Same field set as the
 * manual/label additions to `PolicyDto` above; deliberately does NOT include
 * `payment_frequency` or any other ingest-owned field (this is the manual-Stammdaten edit
 * endpoint, not a general policy editor — §4.5 Option A: no `manual_overrides` array needed
 * because `bezeichnung` is backfill-only and the other three fields are never ingest-written).
 *
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §4.6]
 */
export interface UpdatePolicyDto {
  bezeichnung?: string | null;
  praemienbefreiung?: boolean;
  praemienbefreiung_seit?: string | null;
  vermittler_person_id?: string | null;
  bemerkungen?: string | null;
  /**
   * P2 Placement Slice 2: broker reclassification of the Sparte. A 5-digit IGB2B branchencode
   * validated server-side against `igb2b_kategorie` (400 invalid_igb2b_code). Never cleared to null
   * here (reclassification only sets a real code).
   */
  igb2b_code?: string;
  /** P5-S7: the insurer UID of a policy that has none (set once; 409 carrier_uid_already_set, 400 invalid_carrier_uid). */
  carrier_uid?: string;
}

/**
 * PolicyListItemDto — one Policen-grid row (GET /policies, Story GAP-B4).
 *
 * A superset of `PolicyDto` (ADDITIVE — every existing `PolicyDto[]` consumer is
 * unbroken): the four V10 Kap.-16 grid columns are appended. V10-E visibility
 * stripping is applied server-side at the controller boundary
 * (`applyPolicyListItemVisibilityGate`).
 */
export interface PolicyListItemDto extends PolicyDto {
  /**
   * The CURRENT version's `premium_gross` (the open `policy_version` row,
   * `valid_to IS NULL`; `uq_policy_one_current` guarantees at most one). Numeric
   * string (numeric(14,2) → string, the repo-wide money convention). `null` when
   * the policy has no open version, or when visibility-stripped
   * (`policen.praemie` denied).
   *
   * [Source: packages/db/migrations/0020_policy_core.sql §3]
   */
  premium_gross: string | null;
  /**
   * `app_user.display_name` resolved from `policy.broker_owner` (a nullable
   * app_user UUID). `null` when `broker_owner` is null or the user row is gone.
   * (`app_user` is GLOBAL — no tenant column; funktionsgruppe join precedent.)
   */
  betreuer_name: string | null;
  /**
   * German display label for the Branche/Sparte:
   * `LINE_OF_BUSINESS_LABELS[line_of_business]` when set, else the raw
   * `igb2b_branch` code, else `null`. (Both source fields are already on
   * `PolicyDto` — this is the missing display resolution, not missing data.)
   */
  sparte_label: string | null;
  /**
   * The AVB-15.3 Risikoeinschätzung level. ⚠️ WIRE-READY BUT ALWAYS `null` in
   * this story — there is NO Risikostufe backing data in the schema yet (the
   * AVB-15.3 module, G14, is unbuilt). Filled once that module lands; the
   * `policen.risiko` visibility gate already strips it. The UI must render it
   * null-tolerantly.
   */
  risikostufe: PolicyRisikostufe | null;
  /**
   * The Brokermandat registration this policy was reported with or follows (policy.registration_id),
   * a per-policy exception (versicherer_ausgenommen | broker | vn_abweichend) and the DERIVED
   * registration status with its label (never stored). Optional: the list read carries them, other
   * PolicyDto readers do not.
   */
  registration_id?: string | null;
  registration_exception_reason?: string | null;
  registration_status?: PolicyRegistrationStatus;
  registration_status_label?: string;
  /** The policy number of the policy this one inherited its registration from (Vererbung), else null. */
  registration_inherited_from_policy_number?: string | null;
}

/**
 * GET /policies response envelope (Story GAP-B4 — paginated mode only).
 *
 * DUAL-MODE endpoint: `GET /policies` with NO pagination param returns a bare
 * `PolicyListItemDto[]` (legacy shape, live-web-client-safe); any of
 * `limit`/`cursor`/`include_total` switches the response to THIS envelope.
 */
export interface PolicyListResponseDto {
  items: PolicyListItemDto[];
  page: PageMeta;
}

/**
 * PolicyVersionDto — the policy_version read shape for the 9.2 controller.
 *
 * `policy_version_id` maps to the `id` column (FK target for invoice_check +
 * commission_statement_line in Epics 10/11).
 * `tenant_id` is NOT included (implicit from token).
 * Money columns are strings (numeric(14,2) serialises to string in JSON).
 */
export interface PolicyVersionDto {
  id: string;                      // the FK target: invoice_check.policy_version_id, etc.
  policy_id: string;
  version_nr: number;
  valid_from: string;              // ISO 8601 date string
  valid_to: string | null;         // null = current version
  premium_net: string | null;      // numeric(14,2) → string
  premium_gross: string | null;    // numeric(14,2) → string
  stamp_duty: string | null;       // Stempel-/Versicherungssteuer
  fees: string | null;             // Bearbeitungsgebühren
  coverage_json: Record<string, unknown> | null;
  coverage_delta_json: Record<string, unknown> | null;
  change_reason: string | null;
  source_document_id: string | null;
  created_at: string;
}

// ── Story 9.3 — Adjustments + Endorsements ────────────────────────────────────

/**
 * AdjustmentStatus — mirrors `adjustment_status` Postgres enum.
 * APPEND-ONLY: never remove or reorder values.
 */
export type AdjustmentStatus = 'announced' | 'activated';

/**
 * EndorsementChangeType — mirrors `endorsement_change_type` Postgres enum.
 * Defines what a Nachtrag changes (Prämie, Deckung, or both).
 * APPEND-ONLY: never remove or reorder values.
 */
export type EndorsementChangeType = 'premium' | 'coverage' | 'both';

/**
 * EndorsementStatus — mirrors `endorsement_status` Postgres enum.
 * APPEND-ONLY: never remove or reorder values.
 */
export type EndorsementStatus = 'recorded' | 'pending';

/**
 * AnnouncedAdjustmentDto — the announced_adjustment read shape.
 *
 * `tenant_id` is NOT included (implicit from token — rule #1).
 * Money columns are strings (numeric(14,2) → string; same convention as PolicyVersionDto).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#11]
 * [Source: docs/stories/9.3-adjustments-endorsements.md#AC1]
 */
export interface AnnouncedAdjustmentDto {
  id: string;
  policy_id: string;
  effective_date: string;          // ISO 8601 date string (the Stichtag)
  old_premium: string | null;      // numeric(14,2) → string
  new_premium: string | null;      // numeric(14,2) → string
  status: AdjustmentStatus;
  activated_version_id: string | null;  // populated by activate(); null until activated
  source_document_id: string | null;    // provenance value-link
  created_at: string;
  updated_at: string;
}

/**
 * PolicyEndorsementDto — the policy_endorsement read shape.
 *
 * `tenant_id` is NOT included (implicit from token — rule #1).
 * Money columns are strings (numeric(14,2) → string).
 * `policy_version_id` is the FK target for reproducibility (FIDLEG).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#11]
 * [Source: docs/stories/9.3-adjustments-endorsements.md#AC1]
 */
export interface PolicyEndorsementDto {
  id: string;
  policy_id: string;
  policy_version_id: string;       // FK to the policy_version this Nachtrag PRODUCED
  effective_date: string;          // ISO 8601 date string (Wirkungsdatum)
  change_type: EndorsementChangeType;
  delta_premium: string | null;    // numeric(14,2) → string; set when change_type ∈ {premium, both}
  delta_coverage_json: Record<string, unknown> | null;  // set when change_type ∈ {coverage, both}
  source_document_id: string | null;
  status: EndorsementStatus;
  created_at: string;
}

// ── Story 9.7 — WinPower migration import (normalized migration record shapes) ──

/**
 * MigrationPolicyVersion — one temporal version entry in a migration record.
 *
 * Mirrors AppendVersionDto fields (money as decimal strings; JSON has no decimal type).
 * version_nr ≥ 1; versions are ordered by version_nr ASC in MigrationPolicyRecord.
 *
 * [Source: apps/api/src/modules/policy/dto/append-version.dto.ts]
 * [Source: docs/stories/9.7-winpower-migration-import.md#task-1a]
 */
export interface MigrationPolicyVersion {
  /** 1 = first version; >1 = subsequent versions in order. */
  version_nr: number;
  /** ISO date — version effective date. Must be strictly after the previous version's valid_from for multi-version records. */
  valid_from: string;
  /** ISO date | null (null = current/open version). */
  valid_to?: string | null;
  /** Net premium (CHF, decimal string). */
  premium_net?: string | null;
  /** Gross premium (CHF, decimal string). */
  premium_gross?: string | null;
  /** Stamp duty / Stempelsteuer (CHF, decimal string). */
  stamp_duty?: string | null;
  /** Fees / Bearbeitungsgebühren (CHF, decimal string). */
  fees?: string | null;
  /** Coverage snapshot JSON. */
  coverage_json?: Record<string, unknown> | null;
  /** Change reason — e.g. 'migration'. */
  change_reason?: string | null;
}

/**
 * MigrationWageBasis — reported wage basis for one calendar year.
 *
 * Seeded via WageBasisService.setReportedWage after the policy is created.
 * Only populated for wage-based Sparten (UVG / KTG / BU).
 *
 * [Source: docs/stories/9.7-winpower-migration-import.md#task-1a]
 * [Source: apps/api/src/modules/policy/wage-basis.service.ts (setReportedWage design)]
 */
export interface MigrationWageBasis {
  /** The Geschäftsjahr (calendar year). */
  period_year: number;
  /** Reported Lohnsumme (decimal string). */
  reported_wage_sum: string;
  /** Typed wage categories (reuse WageCategoryEntry from 9.4). */
  categories: WageCategoryEntry[];
  /** true = provisional; false = definitiv (default false). */
  is_provisional?: boolean;
}

/**
 * MigrationPolicyRecord — normalized input record for the WinPower bulk-import seam.
 *
 * Shape: the subset of policy fields the create-or-version core + wage-basis seam read.
 * Money fields as STRINGS (numeric(14,2) → string; JSON has no decimal type).
 *
 * Idempotency key: (tenant_id, carrier_uid, policy_number).
 * - tenant_id comes from the AuthContext (NEVER from the record — rule #1).
 * - carrier_uid + policy_number uniquely identify the policy within the tenant.
 *
 * versions: ≥ 1, ordered by version_nr ASC. version_nr=1 creates the policy;
 *   version_nr>1 appends additional versions (in valid_from order).
 *
 * wage_bases: only for wage-based Sparten (UVG/KTG/BU) — seeded via WageBasisService.
 *
 * [Source: docs/stories/9.7-winpower-migration-import.md#task-1a]
 * [Source: apps/api/src/modules/policy/dto/create-policy.dto.ts]
 */
export interface MigrationPolicyRecord {
  /** Carrier UID (value link to company_master.uid). Nullable if unknown. */
  carrier_uid?: string | null;
  /**
   * Holder (VN / Versicherungsnehmer).
   * subject_id REQUIRED for migration (the broker's existing book has resolved holders).
   */
  holder: { subject_type: 'firma' | 'person'; subject_id: string };
  /** Carrier-issued policy number. Required for idempotency key. */
  policy_number: string;
  /** Line of business / Sparte. */
  line_of_business?: LineOfBusiness | null;
  /** IGB2B branch code (carrier-specific). */
  igb2b_branch?: string | null;
  /** Broker owner UUID. */
  broker_owner?: string | null;
  /** Governing Maklermandat UUID (value link). */
  mandate_id?: string | null;
  /** Policy inception date (ISO 8601 date string). */
  inception_date?: string | null;
  /** Policy expiry date (ISO 8601 date string). */
  expiry_date?: string | null;
  /** ISO 4217 currency code (default 'CHF'). */
  currency?: string | null;
  /** Payment frequency / Zahlweise. */
  payment_frequency?: PaymentFrequency | null;
  /** IBAN for premium payments. */
  iban?: string | null;
  /** QR reference number (Swiss-QR-Bill). */
  qr_reference?: string | null;
  /**
   * Version list — ≥ 1 entry, ordered by version_nr ASC.
   * version_nr=1 is the initial version; subsequent versions are appended in order.
   */
  versions: MigrationPolicyVersion[];
  /**
   * Reported wage bases — only for wage-based Sparten (UVG/KTG/BU).
   * Each entry is seeded via WageBasisService.setReportedWage after the policy is created.
   */
  wage_bases?: MigrationWageBasis[];
}

// ── Story 9.6 — Premium resolution (temporal pro-rata engine) ────────────────

/**
 * PremiumResolutionSource — provenance of a sub-period's premium.
 *
 * Mirrors the three temporal inputs to `PremiumResolutionService.resolveForPeriod`:
 *   'version'     — a plain version boundary (renewal, correction, etc.)
 *   'adjustment'  — the boundary is an activated Stichtag's version
 *                   (version.id === announced_adjustment.activated_version_id)
 *   'endorsement' — the boundary is a Nachtrag's version
 *                   (version.id ∈ policy_endorsement.policy_version_id)
 *
 * APPEND-ONLY union (coding-standards §1 — no magic values at call sites).
 *
 * [Source: docs/stories/9.6-premium-resolution-service.md#task-1a]
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#7.1]
 */
export type PremiumResolutionSource = 'version' | 'adjustment' | 'endorsement';

/**
 * PremiumSubPeriod — one pro-rata sub-period returned by `resolveForPeriod`.
 *
 * Interval convention: `[from, to)` is HALF-OPEN — `from` is inclusive, `to` is
 * exclusive (matches the `policy_version.valid_from / valid_to` semantics in 9.1/9.2).
 * The list from `resolveForPeriod` is contiguous + ordered by `from` ASC.
 *
 * `premium` — the pro-rata CHF amount for this sub-period (numeric(14,2) → string).
 * `policy_version_id` — the version that was effective on this sub-period; the
 *   consuming check (invoice_check.policy_version_id / commission_statement_line.policy_version_id)
 *   stores this id for FINMA/FIDLEG reproducibility.
 * `source` — provenance tag (version / adjustment / endorsement).
 *
 * [Source: docs/stories/9.6-premium-resolution-service.md#task-1a]
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#5.4]
 */
export interface PremiumSubPeriod {
  from: string;                     // ISO 8601 date string, inclusive
  to: string;                       // ISO 8601 date string, exclusive
  premium: string;                  // numeric(14,2) CHF pro-rata amount → string
  policy_version_id: string;        // FK: the version effective on this sub-period
  source: PremiumResolutionSource;  // provenance tag
}

// ── Story 9.4 — Wage basis (Lohnsumme + Lohnkategorien) ──────────────────────

/**
 * WageBasisSource — mirrors `wage_basis_source` Postgres enum.
 * Provenance of the reported wage basis entry.
 * APPEND-ONLY: never remove or reorder values.
 */
export type WageBasisSource = 'manual' | 'migration' | 'document';

/**
 * WageCategoryCode — the typed set of wage category codes for wage-based Sparten.
 * Rechnungskontrolle 6.1: "UVG-BU/UVG-NBU, KTG" — the named categories whose
 * Satz × Lohnsumme is computed per-category in the Schlussabrechnung check.
 * APPEND-ONLY: never remove or reorder values (extend with BU etc. if the spec adds categories).
 */
export type WageCategoryCode = 'UVG-BU' | 'UVG-NBU' | 'KTG';

/**
 * WAGE_BASED_SPARTEN — the canonical list of LineOfBusiness values that carry a wage basis.
 * Single source of truth for "which Sparten require reported Lohnsumme".
 * Used by WageBasisService.setReportedWage gate AND Epic 10's Schlussabrechnung eligibility check.
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#6 (UVG, KTG, BU)]
 * [Source: docs/architecture/coding-standards.md#1 (no magic values — define ONCE here)]
 */
export const WAGE_BASED_SPARTEN: readonly LineOfBusiness[] = ['uvg', 'ktg', 'bu'] as const;

/**
 * WageCategoryEntry — one typed entry in wage_categories_json.
 * category_code is the typed WageCategoryCode union (NOT free text — D9-WAGE-BASIS-IS-FIRST-CLASS).
 * rate and wage are decimal strings (numeric(14,2) → string; JSON has no decimal type).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#6.1
 *   (UVG-BU/UVG-NBU, KTG; Satz × Lohnsumme je Kategorie)]
 */
export interface WageCategoryEntry {
  category_code: WageCategoryCode;  // typed — not free text
  rate: string | null;              // percentage/fraction string (e.g. "0.50" for 0.50%)
  wage: string | null;              // CHF decimal string (e.g. "1000000.00")
}

/**
 * PolicyWageBasisDto — the policy_wage_basis read shape.
 *
 * `tenant_id` is NOT included (implicit from token — rule #1).
 * `period_year` is a number (integer — the Geschäftsjahr).
 * Money columns are strings (numeric(14,2) → string; JSON has no decimal type).
 * `wage_categories_json` is typed as WageCategoryEntry[] (NOT a plain object array).
 * `rate_per_category_json` is {category_code: rate} map or null.
 *
 * `policy_version_id` is nullable — an optional pin to the version whose agreed rates
 * the wage was reported against (see Dev Notes on value-link semantics).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Rechnungskontrolle_V3.txt#11
 *   (settlement_basis: declared_wage_sum, reported_wage_sum, wage_categories_json, rate_per_category_json)]
 * [Source: docs/stories/9.4-wage-basis.md#AC4]
 */
export interface PolicyWageBasisDto {
  id: string;
  policy_id: string;
  policy_version_id: string | null;  // nullable value-link (optional version pin)
  period_year: number;               // the Geschäftsjahr
  reported_wage_sum: string | null;  // numeric(14,2) → string; null until known
  wage_categories_json: WageCategoryEntry[];  // typed structured categories (NOT free text)
  rate_per_category_json: Record<string, string> | null;  // {category_code: rate} map
  is_provisional: boolean;           // true = provisorisch; false = definitiv
  source: WageBasisSource;           // provenance label
  created_at: string;
  updated_at: string;
}
